import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";

/**
 * Build where clause for business search filtering
 * @param {string} searchTerm - Search term for business name
 * @returns {Object} Where clause object
 */
const buildBusinessSearchWhereClause = (searchTerm) => {
  if (!searchTerm || searchTerm.trim() === "") {
    return null;
  }

  return {
    conditions: [
      {
        attribute: "name",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
      {
        attribute: "email",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
      {
        attribute: "phone",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
    ],
    conector: "OR",
  };
};

/**
 * Hook to fetch businesses for products page
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 10)
 * @param {string} params.searchTerm - Search term for business name
 * @returns {Object} Query result with businesses and pagination
 */
export const useBusinessesForProducts = (params = {}) => {
  const { page = 1, pageSize = 10, searchTerm = "" } = params;

  const whereClause = buildBusinessSearchWhereClause(searchTerm);

  const queryParams = {
    params: "name,slug,logo,city(name),address",
    page,
    page_size: pageSize,
  };

  if (whereClause) {
    queryParams.where = whereClause;
  }

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["businesses-products", page, pageSize, searchTerm],
    queryFn: async () => {
      const response = await apiClient.get(
        `/backoffice/businesses${queryString ? `?${queryString}` : ""}`,
      );
      return {
        businesses: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Hook to fetch business by slug
 * @param {string} slug - Business slug
 * @returns {Object} Query result with business details
 */
export const useBusinessBySlug = (slug) => {
  return useQuery({
    queryKey: ["business-by-slug", slug],
    queryFn: async () => {
      const response = await apiClient.get(`/business/${slug}`, {
        params: {
          version: "v2",
          params:
            "id,categories,name,email,slug,schedule,description,about,logo,header,phone,cellphone,owner_id,city_id,address,address_notes,zipcode,location,featured,timezone,currency,food,alcohol,groceries,laundry,use_printer,printer_id,minimum,delivery_price,always_deliver,tax_type,tax,delivery_time,pickup_time,service_fee,fixed_usage_fee,percentage_usage_fee,order_default_priority,cancel_order_after_minutes,enabled,preorder_time,maximum,schedule_ranges,franchise_id,external_id,front_layout,seo_image,seo_title,seo_description,eta_status_times,eta_variation_time,price_level,facebook_profile,instagram_profile,tiktok_profile,snapchat_profile,pinterest_profile,whatsapp_number,delivery_tax_rate,delivery_tax_type,disabled_reason,menus_count,available_menus_count,menus_shared_count,available_menus_shared_count,professionals,configs,checkoutfields,reviews,open,today,lazy_load_products_recommended,available_products_count,valid_service,num_zones,types,metafields,owners,gallery,city,webhooks,maximums,paymethods,ribbon,offers,tax_id,custom_tax,third_party_integrations",
          mode: "dashboard",
        },
      });
      return response?.result;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Hook to toggle category enabled status
 * @returns {Object} Mutation result
 */
export const useToggleCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, categoryId, enabled }) => {
      const response = await apiClient.post(
        `/business/${businessId}/categories/${categoryId}`,
        { enabled },
      );
      return response?.result;
    },
    onMutate: async ({ slug, categoryId, enabled }) => {
      // Optimistic update: update category enabled flag inside business-by-slug cache
      if (!slug) return {};
      await queryClient.cancelQueries({ queryKey: ["business-by-slug", slug] });
      const previousData = queryClient.getQueryData(["business-by-slug", slug]);

      const updateCategoriesTree = (list) => {
        if (!Array.isArray(list)) return list;
        return list.map((cat) => {
          const updated = cat.id === categoryId ? { ...cat, enabled } : cat;
          if (
            Array.isArray(updated.subcategories) &&
            updated.subcategories.length > 0
          ) {
            return {
              ...updated,
              subcategories: updateCategoriesTree(updated.subcategories),
            };
          }
          return updated;
        });
      };

      if (previousData) {
        queryClient.setQueryData(["business-by-slug", slug], {
          ...previousData,
          categories: updateCategoriesTree(previousData.categories || []),
        });
      }

      return { previousData, slug };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.slug && context?.previousData) {
        queryClient.setQueryData(
          ["business-by-slug", context.slug],
          context.previousData,
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", variables.slug],
        });
      }
    },
  });
};

/**
 * Hook to create a new category for a business
 */
export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/categories`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate business-by-slug to refresh categories tree
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", variables.slug],
        });
      }
    },
  });
};

/**
 * Hook to update a category for a business
 */
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, categoryId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/categories/${categoryId}`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate business-by-slug to refresh categories tree
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", variables.slug],
        });
      }
    },
  });
};

/**
 * Hook to delete a category from a business
 */
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, categoryId }) => {
      const response = await apiClient.delete(
        `/business/${businessId}/categories/${categoryId}`,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      const { businessId, categoryId, slug } = variables;

      if (slug) {
        queryClient.invalidateQueries({ queryKey: ["business-by-slug", slug] });
      }

      if (businessId && categoryId) {
        queryClient.invalidateQueries({
          queryKey: ["category-products", businessId, categoryId],
          exact: false,
        });
      }
    },
  });
};

/**
 * Hook to fetch products of a category
 * @param {Object} params - Query parameters
 * @param {number} params.businessId - Business ID
 * @param {number} params.categoryId - Category ID
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 10)
 * @param {string} params.searchTerm - Search term for products
 * @returns {Object} Query result with products and pagination
 */
export const useCategoryProducts = (params = {}) => {
  const {
    businessId,
    categoryId,
    page = 1,
    pageSize = 10,
    searchTerm = "",
  } = params;

  const whereClause = searchTerm
    ? JSON.stringify({
        conditions: [
          {
            attribute: "name",
            value: {
              condition: "ilike",
              value: `%${searchTerm}%`,
            },
          },
          {
            attribute: "description",
            value: {
              condition: "ilike",
              value: `%${searchTerm}%`,
            },
          },
        ],
        conector: "OR",
      })
    : null;

  const queryParams = {
    page,
    page_size: pageSize,
    version: "v2",
  };

  if (whereClause) {
    queryParams.where = whereClause;
  }

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: [
      "category-products",
      businessId,
      categoryId,
      page,
      pageSize,
      searchTerm,
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        `/business/${businessId}/categories/${categoryId}/products${queryString ? `?${queryString}` : ""}`,
      );
      return {
        products: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: !!businessId && !!categoryId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Generic hook to update product fields
 * @param {Object} options - Configuration options
 * @param {boolean} options.optimisticUpdate - Whether to perform optimistic update (default: false)
 * @param {Function} options.optimisticUpdateFn - Function to update cached data optimistically
 * @param {Array<string>} options.invalidateQueries - Additional query keys to invalidate (default: ['category-products'])
 * @param {Function} options.transformPayload - Function to transform payload before sending (payload, { businessId, categoryId, productId }) => transformedPayload
 * @returns {Object} Mutation result
 *
 * @example
 * // Update upselling
 * const mutation = useUpdateProduct();
 * mutation.mutate({ businessId, categoryId, productId, upselling: true });
 *
 * @example
 * // Update tax_id
 * const mutation = useUpdateProduct();
 * mutation.mutate({ businessId, categoryId, productId, tax_id: 137 });
 *
 * @example
 * // Update multiple fields
 * const mutation = useUpdateProduct();
 * mutation.mutate({ businessId, categoryId, productId, upselling: true, tax_id: 137 });
 *
 * @example
 * // With optimistic update
 * const mutation = useUpdateProduct({
 *   optimisticUpdate: true,
 *   optimisticUpdateFn: ({ businessId, categoryId, productId, payload, queryClient }) => {
 *     // Custom optimistic update logic
 *   }
 * });
 *
 * @example
 * // With payload transformation
 * const mutation = useUpdateProduct({
 *   transformPayload: (payload, { businessId, categoryId, productId }) => {
 *     return { ...payload, extras: JSON.stringify(payload.extras) };
 *   }
 * });
 */
export const useUpdateProduct = (options = {}) => {
  const {
    optimisticUpdate = false,
    optimisticUpdateFn,
    invalidateQueries = ["category-products"],
    transformPayload,
  } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, categoryId, productId, ...payload }) => {
      // Transform payload if transformation function is provided
      const finalPayload = transformPayload
        ? transformPayload(payload, { businessId, categoryId, productId })
        : payload;
      // Remove businessId, categoryId, productId from payload as they're used in the URL
      const response = await apiClient.post(
        `/business/${businessId}/categories/${categoryId}/products/${productId}`,
        finalPayload,
      );
      return response?.result;
    },
    onMutate:
      optimisticUpdate && optimisticUpdateFn
        ? async (variables) => {
            const { businessId, categoryId, productId, ...payload } = variables;
            return await optimisticUpdateFn({
              businessId,
              categoryId,
              productId,
              payload,
              queryClient,
            });
          }
        : undefined,
    onError:
      optimisticUpdate && optimisticUpdateFn
        ? (_err, _vars, context) => {
            // Rollback optimistic update on error
            if (context?.previousEntries) {
              context.previousEntries.forEach(([key, oldData]) => {
                queryClient.setQueryData(key, oldData);
              });
            }
          }
        : undefined,
    onSuccess: () => {
      // Invalidate specified queries
      invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
          exact: false,
        });
      });
    },
  });
};

/**
 * Hook to create a new product
 * @returns {Object} Mutation result
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, categoryId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/categories/${categoryId}/products`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate category-products queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: [
          "category-products",
          variables.businessId,
          variables.categoryId,
        ],
      });
    },
  });
};

/**
 * Hook to delete a product
 * @returns {Object} Mutation result
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, categoryId, productId }) => {
      const response = await apiClient.delete(
        `/business/${businessId}/categories/${categoryId}/products/${productId}`,
      );
      return response?.result;
    },
    onMutate: async ({ businessId, categoryId, productId }) => {
      // Optimistic update: remove product from all cached pages for this category
      const previousEntries = [];
      const allQueries = queryClient.getQueriesData({
        queryKey: ["category-products"],
      });
      allQueries.forEach(([key, data]) => {
        // key format: ['category-products', businessId, categoryId, page, pageSize, searchTerm]
        if (!Array.isArray(key)) return;
        const [, qBusinessId, qCategoryId] = key;
        if (
          qBusinessId === businessId &&
          qCategoryId === categoryId &&
          data?.products
        ) {
          const updatedProducts = data.products.filter(
            (p) => p.id !== productId,
          );
          if (updatedProducts.length !== data.products.length) {
            previousEntries.push([key, data]);
            queryClient.setQueryData(key, {
              ...data,
              products: updatedProducts,
            });
          }
        }
      });
      return { previousEntries };
    },
    onError: (_err, _vars, context) => {
      // Rollback cached lists on error
      if (context?.previousEntries) {
        context.previousEntries.forEach(([key, oldData]) => {
          queryClient.setQueryData(key, oldData);
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      // Invalidate all category-products queries to refresh the list
      // This ensures the UI updates even if optimistic update fails
      queryClient.invalidateQueries({
        queryKey: [
          "category-products",
          variables.businessId,
          variables.categoryId,
        ],
      });
    },
  });
};

/**
 * Hook to fetch extras of a business
 * @param {Object} params - Query parameters
 * @param {number} params.businessId - Business ID
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 10)
 * @returns {Object} Query result with extras and pagination
 */
export const useBusinessExtras = (params = {}) => {
  const { businessId } = params;

  const queryParams = {
    mode: "dashboard",
    params:
      "id,extras,business_id,name,description,enabled,external_id,rank,metafields",
    page_size: 1000,
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["business-extras", businessId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/business/${businessId}/extras${queryString ? `?${queryString}` : ""}`,
      );
      return {
        extras: response?.result || [],
      };
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Hook to fetch options for an extra
 * @param {Object} params
 * @param {number} params.businessId
 * @param {number} params.extraId
 * @param {number} params.page
 * @param {number} params.pageSize
 */
export const useExtraOptions = (params = {}) => {
  const { businessId, extraId, page = 1, pageSize = 50 } = params;

  return useQuery({
    queryKey: ["extra-options", businessId, extraId, page, pageSize],
    queryFn: async () => {
      const response = await apiClient.get(
        `/business/${businessId}/extras/${extraId}/options`,
        {
          params: {
            mode: "dashboard",
            page,
            page_size: pageSize,
          },
        },
      );
      return {
        options: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: !!businessId && !!extraId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Toggle extra option enabled
 */
export const useToggleExtraOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, option }) => {
      const { id, name, min, max, enabled } = option;
      const response = await apiClient.post(
        `/business/${businessId}/extras/${extraId}/options/${id}`,
        {
          name,
          min,
          max,
          enabled,
        },
      );
      return response?.result;
    },
    onMutate: async ({ businessId, extraId, option }) => {
      const keyPrefix = ["extra-options"];
      const previousEntries = [];
      const queries = queryClient.getQueriesData({ queryKey: keyPrefix });
      queries.forEach(([key, data]) => {
        if (!Array.isArray(key)) return;
        const [, qBusinessId, qExtraId] = key;
        if (
          qBusinessId === businessId &&
          qExtraId === extraId &&
          data?.options
        ) {
          previousEntries.push([key, data]);
          queryClient.setQueryData(key, {
            ...data,
            options: data.options.map((op) =>
              op.id === option.id ? { ...op, enabled: option.enabled } : op,
            ),
          });
        }
      });
      return { previousEntries };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousEntries) {
        ctx.previousEntries.forEach(([key, oldData]) =>
          queryClient.setQueryData(key, oldData),
        );
      }
    },
    onSettled: (_d, _e, { businessId, extraId }) => {
      queryClient.invalidateQueries({
        queryKey: ["extra-options", businessId, extraId],
      });
    },
  });
};

/**
 * Toggle extra suboption enabled
 */
export const useToggleExtraSuboption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, optionId, suboption }) => {
      const response = await apiClient.post(
        `/business/${businessId}/extras/${extraId}/options/${optionId}/suboptions/${suboption.id}`,
        {
          id: suboption.id,
          enabled: suboption.enabled,
        },
      );
      return response?.result;
    },
    onMutate: async ({ businessId, extraId, optionId, suboption }) => {
      const previousEntries = [];
      const queries = queryClient.getQueriesData({
        queryKey: ["extra-options"],
      });
      queries.forEach(([key, data]) => {
        if (!Array.isArray(key)) return;
        const [, qBusinessId, qExtraId] = key;
        if (
          qBusinessId === businessId &&
          qExtraId === extraId &&
          data?.options
        ) {
          previousEntries.push([key, data]);
          queryClient.setQueryData(key, {
            ...data,
            options: data.options.map((op) =>
              op.id === optionId
                ? {
                    ...op,
                    suboptions: (op.suboptions || []).map((so) =>
                      so.id === suboption.id
                        ? { ...so, enabled: suboption.enabled }
                        : so,
                    ),
                  }
                : op,
            ),
          });
        }
      });
      return { previousEntries };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previousEntries) {
        ctx.previousEntries.forEach(([key, oldData]) =>
          queryClient.setQueryData(key, oldData),
        );
      }
    },
    onSettled: (_d, _e, { businessId, extraId }) => {
      queryClient.invalidateQueries({
        queryKey: ["extra-options", businessId, extraId],
      });
    },
  });
};

/**
 * Hook to create a new suboption for an option
 * @returns {Object} Mutation result
 */
export const useCreateExtraSuboption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, optionId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/extras/${extraId}/options/${optionId}/suboptions`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all extra-options queries for this business/extra (regardless of page/pageSize)
      queryClient.invalidateQueries({
        queryKey: ["extra-options", variables.businessId, variables.extraId],
        exact: false,
      });
    },
  });
};

/**
 * Hook to update a suboption for an option
 * @returns {Object} Mutation result
 */
export const useUpdateExtraSuboption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      businessId,
      extraId,
      optionId,
      suboptionId,
      payload,
    }) => {
      const response = await apiClient.post(
        `/business/${businessId}/extras/${extraId}/options/${optionId}/suboptions/${suboptionId}`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all extra-options queries for this business/extra (regardless of page/pageSize)
      queryClient.invalidateQueries({
        queryKey: ["extra-options", variables.businessId, variables.extraId],
        exact: false,
      });
    },
  });
};

/**
 * Hook to delete a suboption from an option
 * @returns {Object} Mutation result
 */
export const useDeleteExtraSuboption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, optionId, suboptionId }) => {
      const response = await apiClient.delete(
        `/business/${businessId}/extras/${extraId}/options/${optionId}/suboptions/${suboptionId}`,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all extra-options queries for this business/extra (regardless of page/pageSize)
      queryClient.invalidateQueries({
        queryKey: ["extra-options", variables.businessId, variables.extraId],
        exact: false,
      });
    },
  });
};

/**
 * Hook to create a new option for an extra
 * @returns {Object} Mutation result
 */
export const useCreateExtraOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/extras/${extraId}/options`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all extra-options queries for this business/extra (regardless of page/pageSize)
      queryClient.invalidateQueries({
        queryKey: ["extra-options", variables.businessId, variables.extraId],
        exact: false,
      });
    },
  });
};

/**
 * Hook to update an option from an extra
 * @returns {Object} Mutation result
 */
export const useUpdateExtraOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, optionId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/extras/${extraId}/options/${optionId}`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all extra-options queries for this business/extra (regardless of page/pageSize)
      queryClient.invalidateQueries({
        queryKey: ["extra-options", variables.businessId, variables.extraId],
        exact: false,
      });
    },
  });
};

/**
 * Hook to delete an option from an extra
 * @returns {Object} Mutation result
 */
export const useDeleteExtraOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, optionId }) => {
      const response = await apiClient.delete(
        `/business/${businessId}/extras/${extraId}/options/${optionId}`,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all extra-options queries for this business/extra (regardless of page/pageSize)
      queryClient.invalidateQueries({
        queryKey: ["extra-options", variables.businessId, variables.extraId],
        exact: false,
      });
    },
  });
};

/**
 * Hook to create a new extra for a business
 * @returns {Object} Mutation result
 */
export const useCreateExtra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, payload }) => {
      const response = await apiClient.post(
        `/business/${businessId}/extras`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate business-extras queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["business-extras", variables.businessId],
      });
    },
  });
};

/**
 * Hook to update an extra
 * @returns {Object} Mutation result
 */
export const useUpdateExtra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId, payload }) => {
      const response = await apiClient.put(
        `/business/${businessId}/extras/${extraId}`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate business-extras queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["business-extras", variables.businessId],
      });
    },
  });
};

/**
 * Hook to delete an extra from a business
 * @returns {Object} Mutation result
 */
export const useDeleteExtra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, extraId }) => {
      const response = await apiClient.delete(
        `/business/${businessId}/extras/${extraId}`,
      );
      return response?.result;
    },
    onMutate: async ({ businessId, extraId }) => {
      // Optimistic update: remove extra from all cached pages
      const previousEntries = [];
      const allQueries = queryClient.getQueriesData({
        queryKey: ["business-extras"],
      });
      allQueries.forEach(([key, data]) => {
        // key format: ['business-extras', businessId, page, pageSize]
        if (!Array.isArray(key)) return;
        const [, qBusinessId] = key;
        if (qBusinessId === businessId && data?.extras) {
          const updatedExtras = data.extras.filter((e) => e.id !== extraId);
          if (updatedExtras.length !== data.extras.length) {
            previousEntries.push([key, data]);
            queryClient.setQueryData(key, {
              ...data,
              extras: updatedExtras,
            });
          }
        }
      });
      return { previousEntries };
    },
    onError: (_err, _vars, context) => {
      // Rollback cached lists on error
      if (context?.previousEntries) {
        context.previousEntries.forEach(([key, oldData]) => {
          queryClient.setQueryData(key, oldData);
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      // Invalidate all business-extras queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["business-extras", variables.businessId],
      });
    },
  });
};

export default {
  useBusinessesForProducts,
  useBusinessBySlug,
  useToggleCategory,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useCategoryProducts,
  useUpdateProduct,
  useCreateProduct,
  useDeleteProduct,
  useBusinessExtras,
  useExtraOptions,
  useToggleExtraOption,
  useToggleExtraSuboption,
  useCreateExtraSuboption,
  useUpdateExtraSuboption,
  useDeleteExtraSuboption,
  useCreateExtraOption,
  useUpdateExtraOption,
  useDeleteExtraOption,
  useCreateExtra,
  useUpdateExtra,
  useDeleteExtra,
};
